import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { HighlightColor } from '../highlight-color.model';
import { AnnotationService } from '@services/annotation.service';
import { RedactionAnnotationEdit } from '../redaction-annotation-edit.model';
import { LifeCycleEdit } from '../life-cycle-edit.enum';
import { RedactionService } from '@services/redaction.service';
import { MatSelect } from '@angular/material/select';

@Component({
    selector: 'lex-page-edits-view',
    templateUrl: './page-edits-view.component.html',
    styleUrls: ['./page-edits-view.component.scss'],
    standalone: false
})
export class PageEditsViewComponent implements OnChanges {

	@Input() active: boolean;
	@Input() pageNumber: number;
	@Input() isRedactionEditor: boolean;
	@Input() edits: RedactionAnnotationEdit[];
	@Input() selectedId: number;

	@Output() pageNumberChanged: EventEmitter<number> = new EventEmitter<number>();
	@Output() editModified: EventEmitter<RedactionAnnotationEdit> = new EventEmitter<RedactionAnnotationEdit>();
	@Output() redactCurrentPageClicked: EventEmitter<number> = new EventEmitter<number>();
	@Output() editSelected: EventEmitter<number> = new EventEmitter<number>();

	@ViewChild('redactionColorSelect') redactionColorSelect: MatSelect;
	@ViewChild('annotationColorSelect') annotationColorSelect: MatSelect;

	initialized: boolean = false;
	highlightColors: HighlightColor[] = [];
	colorDisplayNames: Map<string, string> = new Map([
		['1a3bcf6e-e844-e911-a9db-124a916a6f54', 'Yellow'],
		['00e23c75-e844-e911-a9db-124a916a6f54', 'Orange'],
		['01e23c75-e844-e911-a9db-124a916a6f54', 'Green'],
		['d003ae7e-e844-e911-a9db-124a916a6f54', 'Blue'],
		['d103ae7e-e844-e911-a9db-124a916a6f54', 'Pink']
	]);
	redactionDisplayNames: Map<string, string> = new Map([
		['69b9cfdb-6f1a-4631-bb51-d1d5a30ce1aa', 'Black'],
		['a2d86281-46c8-4a12-9cf5-f665c5e89e57', 'White']
	]);
	pagesAndEdits: Array<{ pageNum: number, edits: RedactionAnnotationEdit[] }> = new Array<{ pageNum: number, edits: RedactionAnnotationEdit[] }>();
	displayColumns = ['text', 'color', 'delete'];
	editItemTextId: number = null;
	editItemColorId: number = null;
	redactionColors: HighlightColor[] = [];

	constructor(private annotationService: AnnotationService, private redactionService: RedactionService) {
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.edits)
			this.fillPagesAndEdits(this.edits);

		if (changes.selectedId && changes.selectedId.currentValue != changes.selectedId.previousValue) {
			this.editItemColorId = null;
		}

		if (this.active && !this.initialized) {
			if (this.isRedactionEditor)
				this.redactionService.getRedactionColors('redaction').subscribe((data: HighlightColor[]) => {
					this.redactionColors = data;
				});
			else
				this.annotationService.getHighlightColors('annotation').subscribe((data: HighlightColor[]) => {
					this.highlightColors = data;
				});

			this.initialized = true;
		}
	}

	goToPage(pageNum: number) {
		this.selectedId = null;
		this.editSelected.emit(null);
		this.pageNumberChanged.emit(pageNum);
	}

	updateEdit(edit: RedactionAnnotationEdit) {
		if (edit.status != LifeCycleEdit.Created)
			edit.status = LifeCycleEdit.Modified;
		this.editModified.emit(edit);
	}

	deleteEdit(edit: RedactionAnnotationEdit) {
		edit.status = LifeCycleEdit.Deleted;
		this.editModified.emit(edit);
		this.editSelected.emit(null);
	}

	onClickEditExpand(edit: RedactionAnnotationEdit) {
		this.selectedId = (this.selectedId == edit.clientId) ? null : edit.clientId;
	}

	getColor(color: HighlightColor): string {
		if (this.isRedactionEditor)
			return this.redactionDisplayNames.get(color.highlightColorId);
		else
			return this.colorDisplayNames.get(color.highlightColorId);
	}

	setColor(event: any, edit: RedactionAnnotationEdit) {
		edit.highlightColor = this.isRedactionEditor ? this.redactionColors.find(c => c.highlightColorId == event.value).colorValue : this.highlightColors.find(c => c.highlightColorId == event.value).colorValue;
		edit.highlightColorId = event.value;
		this.updateEdit(edit);
	}

	getBackGroundColor(colorValue: string): string {
		return `#${colorValue}`;
	}

	fillPagesAndEdits(edits: RedactionAnnotationEdit[]) {
		const existingRedactionAnnotations = edits?.sort((a: RedactionAnnotationEdit, b: RedactionAnnotationEdit) => {
			if (a.y === b.y)
				if (a.x === b.x)
					return a.clientId > b.clientId ? 1 : -1;
				else
					return a.x > b.x ? 1 : -1;
			return a.y > b.y ? 1 : -1;
		});
		this.pagesAndEdits = [];
		existingRedactionAnnotations?.forEach(r => {
			if (this.pagesAndEdits.findIndex(e => e.pageNum == r.rank) != -1)
				this.pagesAndEdits.find(e => e.pageNum == r.rank).edits.push(r);
			else
				this.pagesAndEdits.push({
					pageNum: r.rank,
					edits: [r]
				});
		});
		this.pagesAndEdits?.sort((a, b) => a.pageNum > b.pageNum ? 1 : -1);
	}

	getEdits(edits: RedactionAnnotationEdit[]): RedactionAnnotationEdit[] {
		if (!edits) return [];
		return edits.filter(e => e.status != LifeCycleEdit.Deleted);
	}

	getActiveEditCount(edits: RedactionAnnotationEdit[]): number {
		return edits.filter(e => e.status != LifeCycleEdit.Deleted).length;
	}

	setSelectedEdit(id: number) {
		this.selectedId = id;
		this.editSelected.emit(this.selectedId);
	}

	async openSelect(element: RedactionAnnotationEdit) {
		this.editItemColorId = element.clientId;
		await this.delay(100);
		if (this.isRedactionEditor)
			this.redactionColorSelect.open();
		else
			this.annotationColorSelect.open();
	}

	delay(ms: number) {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	openChanged(opened: boolean) {
		if (opened) return;

		this.editItemColorId = null;
	}

	changeActiveItem(v: any) {
		this.editItemTextId = v?.clientId;
	}

	trackById(index: number, value: RedactionAnnotationEdit): number {
		return value.clientId;
	}

	trackByPageNumber(index: number, value: any): number {
		return value.pageNum;
	}
}
